import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  GlobalStyles,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import TableContainer, { TableContainerProps } from '@mui/material/TableContainer';
import makeStyles from '@mui/styles/makeStyles';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { SWLoadingButton } from '@/components/LoadingButton';
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import NoRows from '@/components/tableNoRows';
import { Unit as UnitType } from '@/Units/types';
import { log, naturalCompare } from '@/util';

import { calculateDueDateFromToday } from '../dueDateUtils';
import {
  deleteFeeTemplate,
  FeeTemplate,
  FeeTemplateType,
  listFeeTemplates,
  saveFeeTemplate,
} from '../feeTemplateApi';

dayjs.extend(utc);
dayjs.extend(timezone);

// style for the dialog paper/modal
const useStyles = makeStyles({
  paper: {
    minHeight: 560,
    borderRadius: 10,
  },
});

const UNIT_LABELS: Record<
  'GALLONS' | 'GALLONS_100' | 'GALLONS_1K' | 'LITERS' | 'LITERS_1K' | 'CF' | 'CCF',
  string
> = {
  GALLONS: 'Gallons',
  GALLONS_100: '100 Gallons',
  GALLONS_1K: '1000 Gallons',
  LITERS: 'Liters',
  LITERS_1K: '1000 Liters',
  CF: 'CF (Cubic Feet)',
  CCF: 'CCF (Centum Cubic Feet)',
};

const FEE_TYPE_LABELS: Record<string, string> = {
  SPLIT: 'Split Fee',
  FLAT: 'Flat Fee',
  UNIT_FLAT: 'Unit-Flat Fee',
};

const FEE_TYPE_DESC: Record<string, string> = {
  SPLIT:
    'Divides the total fee between your tenants. Each party pays a portion of the billing or service fee. Examples include fixed sewer fees, infrastructure costs, or other fixed-line items from your utility bill.',
  FLAT: 'Single, fixed charge billed to all of your tenants regardless of the number of units, residents, or the total amount collected. Examples include billing or convenience fees, trash fees, etc.',
  UNIT_FLAT:
    'One-time charge applied to a single unit. Examples include late fees or excessive water usage fees.',
};

// for a range of dates
const StyledRange = styled(DatePicker.RangePicker)(({ theme }) => ({
  // pill container
  '&&': {
    width: 625,
    height: 45,
    border: `1px solid ${theme.palette.grey[400]}`, // rgba(0,0,0,0.23)
    borderRadius: 14,
    position: 'relative',
    overflow: 'hidden',
  },

  // small gutter around the arrow
  '&& .ant-picker-separator': {
    flex: '0 0 auto',
    margin: '0 16px',
    display: 'flex',
    alignItems: 'center',
  },

  // calendar icon
  '&& .ant-picker-suffix': {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  },
}));

// for a single styled date
const StyledDate = styled(DatePicker)(({ theme }) => ({
  '&&': {
    width: 300,
    height: 45,
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: 14,
    position: 'relative',
    overflow: 'hidden',
  },
  '&& .ant-picker-suffix': {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  },
}));

const StyledFeesTable = styled(TableContainer)<TableContainerProps>(({ theme }) => ({
  boxShadow: 'none',
  marginTop: theme.spacing(2),
}));

const FormInputWrapper = styled(Box)({
  paddingTop: 8,
  paddingBottom: 8,
});

const InputRowWrapper = styled(Box)({
  display: 'flex',
  gap: 24,
  alignItems: 'baseline',
});

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: Function;
  start?: number;
  end?: number;
  cost?: number;
  volume?: number;
  unit?: 'GALLONS' | 'GALLONS_100' | 'GALLONS_1K' | 'LITERS' | 'LITERS_1K' | 'CF' | 'CCF';
  type: 'ADD' | 'EDIT';
  rateMethod?: 'SIMPLESUB' | 'UTILITY' | 'CUSTOM';
  effectiveRate?: number | null;
  loading: boolean;
  propertyTimeZone: string;
  stripeConnect: boolean;
  units: UnitType[];
  volumeUnits: string;
  dueDateDays?: number | null;
  existingFees?: { name: string; feeType: string; cost: number; unitId?: string }[];
};

export interface FormValues {
  startDate: number | null;
  endDate: number | null;
  volume: number | null;
  unit: string;
  cost: number | null;
  rateMethod: string;
  effectiveRate: number | null;
  dueDate: number;
  fees: { name: string; feeType: string; cost: number; unitId?: string }[];
  eUsage: IEstimadeUsage[];
}

const METHOD_DESCRIPTION = {
  SIMPLESUB:
    'This is the recommended rate method if all water to a property is metered by SimpleSUB Water.  The cost of the utility bill will be divided based on usage, so you’ll never over or under bill tenants.  Effective Rate =  Cost / SimpleSUB Measured Total Water Usage',
  UTILITY:
    'This is the recommended rate method if not all water to a property is metered by SimpleSUB Water. An effective rate is calculated based on the water bill. Effective Rate = Cost / Utility Measured Total Water Usage.',
  CUSTOM:
    'Use this method to calculate tenant costs based on a fixed rate from the utility. Warning: Be careful never to overbill for water!',
};

const billingFormValidationSchema = Yup.object().shape({
  startDate: Yup.number().required('Required!'),
  endDate: Yup.number().required('Required!'),
  volume: Yup.number().required('Required!'),
  unit: Yup.string().required('Required!'),
  cost: Yup.number().required('Required!'),
  rateMethod: Yup.string().required('Required!'),
  effectiveRate: Yup.number()
    .nullable()
    .when('rateMethod', {
      is: (val: string) => val === 'CUSTOM',
      then: (schema) => schema.required('Required!'),
      otherwise: (schema) => schema.notRequired(),
    }),
});

interface IEstimadeUsage {
  deviceId: string;
  usage: string;
}

export const FormDialog = ({
  start,
  end,
  volume,
  unit,
  cost,
  open,
  type,
  onClose,
  onSubmit,
  rateMethod,
  effectiveRate,
  loading = false,
  propertyTimeZone,
  stripeConnect,
  units,
  volumeUnits: _volumeUnits, // destructuring alias to avoid unused variable warning
  dueDateDays,
  existingFees = [],
}: Props) => {
  const [openFee, setOpenFee] = React.useState(false);

  const [newFeeData, setNewFeeData] = React.useState<{
    feeType: string;
    name: string;
    cost: string;
    unitIds: string[];
  }>({
    feeType: 'FLAT',
    name: '',
    cost: '',
    unitIds: [],
  });
  const [newFeeErr, setNewFeeErr] = useState(false);
  const [noDevices, setnoDevices] = useState(false);
  const [estimatedUsage, setEstimatedUsage] = React.useState<IEstimadeUsage[]>([]);
  const [steps, setSteps] = React.useState(['Enter Bill', 'Add Fees']);
  const { structureId } = useParams();
  if (!structureId) {
    throw new Error('MISSING_REQUIRED_ROUTE_DATA');
  }

  // Saved fee templates for this structure: listed in the Add Fee menu for
  // one-click re-add, saved from the Add Fee dialog's checkbox. Templates only
  // prefill the fees table; the bill itself always submits plain line items.
  const [savedFees, setSavedFees] = React.useState<FeeTemplate[]>([]);
  const [saveFeeForFuture, setSaveFeeForFuture] = React.useState(false);
  // Saved fees are managed (deleted) in their own dialog rather than inside the
  // Add Fee menu: menu items must stay single-action and keyboard-operable, and
  // a nested delete control inside a menuitem is reachable only by pointer.
  const [openManageFees, setOpenManageFees] = React.useState(false);
  // Two-step delete: first activation arms the row, second confirms.
  const [deleteArmedId, setDeleteArmedId] = React.useState<string | null>(null);
  // Set when Create Bill is blocked by an incomplete fee row, so the reason is
  // shown at the field and in a summary alert instead of a silent no-op.
  const [showFeeErrors, setShowFeeErrors] = React.useState(false);

  const refreshSavedFees = React.useCallback(() => {
    listFeeTemplates(structureId)
      .then(setSavedFees)
      .catch((err) => log.error(err));
  }, [structureId]);

  React.useEffect(() => {
    if (open) refreshSavedFees();
  }, [open, refreshSavedFees]);

  const handleClickMenu = () => {
    setOpenFee(true);
  };

  const handleAddSavedFee = (template: FeeTemplate) => {
    if (template.type === 'UNIT_FLAT') {
      // Unit scope is never stored on a template (units churn between bills),
      // so a unit-flat fee re-prompts for units with the rest prefilled.
      setNewFeeData({
        feeType: 'UNIT_FLAT',
        name: template.description,
        cost: String(template.cost),
        unitIds: [],
      });
      setOpenFee(true);
      return;
    }
    handleAddFee(
      { name: template.description, feeType: template.type, cost: Number(template.cost) },
      true,
    );
  };

  const handleDeleteSavedFee = (template: FeeTemplate) => {
    setDeleteArmedId(null);
    deleteFeeTemplate(structureId, template.id)
      .then(refreshSavedFees)
      .catch((err) => {
        log.error(err);
        toast.error('Could not remove the saved fee. Please try again.');
      });
  };

  const handleManageFeesClose = () => {
    setDeleteArmedId(null);
    setOpenManageFees(false);
  };

  // whenever feeType changes away from “UNIT_FLAT”, clear the old unitIds
  React.useEffect(() => {
    if (newFeeData.feeType !== 'UNIT_FLAT') {
      setNewFeeData((d) => ({ ...d, unitIds: [] }));
    }
  }, [newFeeData.feeType]);

  React.useEffect(() => {
    // Both Stripe and non-Stripe accounts can now add fees
    setSteps(['Enter Bill', 'Add Fees']);
  }, [setSteps]);

  React.useEffect(() => {
    const initialUsage = units.flatMap((unit) => {
      if (!unit.devices) return [];
      return unit.devices
        .filter((device) => device.device_status !== 'OKAY')
        .map((i) => {
          return {
            deviceId: i.id,
            usage: '0.0',
          };
        });
    });

    setEstimatedUsage(initialUsage);
  }, [units]);

  React.useEffect(() => {
    const hasDevices = units.some((unit) => unit.devices && unit.devices.length > 0);
    setnoDevices(!hasDevices);
  }, [units]);

  const browserTimeZone = dayjs.tz.guess();
  const timeZone = propertyTimeZone.trim() === '' ? browserTimeZone : propertyTimeZone;
  dayjs.tz.setDefault(timeZone);
  const [activeStep, setActiveStep] = React.useState(0);

  const formik = useFormik<FormValues>({
    initialValues: {
      startDate: start || null,
      endDate: end || null,
      volume: volume || null,
      unit: unit || 'GALLONS_1K',
      cost: cost || null,
      rateMethod: rateMethod || 'SIMPLESUB',
      effectiveRate: effectiveRate || null,
      dueDate: calculateDueDateFromToday(dueDateDays ?? 21, timeZone),
      fees: existingFees || [],
      eUsage: [],
    },
    onSubmit: async (values) => {
      await onSubmit(values);
    },
    validationSchema: billingFormValidationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
  });
  const handleNext = async () => {
    // step-0 validation...
    const errors = await formik.validateForm();
    // now errors.effectiveRate will be set if you have CUSTOM + no effectiveRate
    if (
      errors.startDate ||
      errors.endDate ||
      errors.volume ||
      errors.cost ||
      errors.effectiveRate
    ) {
      return; // don't advance
    }

    // 1) If we're on activeStep 0, validate those fields and bail if anything is missing
    if (activeStep === 0) {
      const errors = await formik.validateForm();
      // only check the fields shown on step 0
      if (
        errors.startDate ||
        errors.endDate ||
        errors.volume ||
        errors.cost ||
        errors.effectiveRate
      ) {
        return; // do not advance
      }
    }

    // 2) Both ADD and EDIT modes now use the same flow: advance through steps
    if (activeStep + 1 < steps.length) {
      setActiveStep(activeStep + 1);
      return;
    }

    // 3) Final step - submit the form
    // Inline-edited fees must be complete before the bill is created. Blocking
    // silently reads as a dead button, so surface the reason at the row AND in
    // a summary alert near the action.
    const hasInvalidFee = formik.values.fees.some(
      (fee) => fee.name.trim() === '' || (fee.feeType === 'UNIT_FLAT' && !fee.unitId),
    );
    if (hasInvalidFee) {
      setShowFeeErrors(true);
      return;
    }
    setShowFeeErrors(false);
    await formik.setFieldValue('eUsage', estimatedUsage);
    await setEstimatedUsage([]);
    return handleSubmit();
  };

  // clears errors and resets form values when closing the "Add Bill" dialog
  const handleClose = () => {
    // reset values/errors/touched back to initial
    formik.resetForm();
    // reset step
    setActiveStep(0);
    onClose();
  };
  // clears errors and resets form values when closing the "Add Fee" dialog
  const handleFeeClose = () => {
    setNewFeeErr(false);
    setNewFeeData({ feeType: 'FLAT', name: '', cost: '', unitIds: [] });
    setSaveFeeForFuture(false);
    setOpenFee(false);
  };

  const classes = useStyles();

  React.useEffect(() => {
    if (open) {
      formik.resetForm();
      setActiveStep(0);
      formik.setValues({
        startDate: start || null,
        endDate: end || null,
        volume: volume || null,
        unit: unit || 'GALLONS_1K',
        cost: cost || null,
        rateMethod: rateMethod || 'SIMPLESUB',
        effectiveRate: effectiveRate || null,
        dueDate: calculateDueDateFromToday(dueDateDays ?? 21, timeZone),
        fees: existingFees || [],
        eUsage: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDateChange = (dates) => {
    formik.setFieldValue(
      'startDate',
      dayjs
        .tz(dates[0], timeZone)
        .set('hours', 0)
        .set('seconds', 0)
        .set('minutes', 0)
        .set('milliseconds', 0)
        .valueOf(),
    );
    formik.setFieldValue(
      'endDate',
      dayjs
        .tz(dates[1], timeZone)
        .set('hours', 23)
        .set('seconds', 59)
        .set('minutes', 59)
        .set('milliseconds', 0)
        .valueOf(),
    );
  };

  const handleDueDateChange = (date) => {
    formik.setFieldValue(
      'dueDate',
      dayjs
        .tz(date, timeZone)
        .set('hours', 23)
        .set('seconds', 59)
        .set('minutes', 59)
        .set('milliseconds', 0)
        .valueOf(),
    );
  };

  const handleSubmit = async () => {
    const errors = await formik.validateForm();

    if (
      Object.keys(errors).length > 0 ||
      (formik.values.rateMethod === 'CUSTOM' && formik.values.effectiveRate === null)
    ) {
      setActiveStep(0);
      return;
    }

    formik.submitForm();
  };

  const handleAddFee = (data?: any, fee?: boolean) => {
    // 1) If it’s coming from an existing fee item (menu pick), just push it through
    if (fee) {
      formik.setFieldValue('fees', [...formik.values.fees, data]);
      return;
    }

    // 2) If it’s coming from a preset line‐item with its own unitId, push it
    if (!fee && data.unitId) {
      formik.setFieldValue('fees', [...formik.values.fees, data]);
      return;
    }

    // 3) Otherwise it’s our “new new” fee, so validate name, cost, unitIds
    if (
      newFeeData.name.trim() === '' ||
      newFeeData.cost.trim() === '' ||
      (newFeeData.feeType === 'UNIT_FLAT' && newFeeData.unitIds.length === 0)
    ) {
      setNewFeeErr(true);
      return;
    }

    // 4) Parse cost to a real number
    const costNumber = parseFloat(newFeeData.cost);
    if (isNaN(costNumber)) {
      // cost wasn’t a valid number
      setNewFeeErr(true);
      return;
    }

    // 5) Commit into Formik. UNIT_FLAT supports multi-select: each selected unit
    // gets its own line item (the BillingLineItem schema only allows one unit_id
    // per row, so the dialog expands one user-facing "fee" into N line items
    // with identical name/cost). FLAT and SPLIT carry no unitId and stay as a
    // single row.
    const newLineItems =
      newFeeData.feeType === 'UNIT_FLAT'
        ? newFeeData.unitIds.map((unitId) => ({
            feeType: newFeeData.feeType,
            name: newFeeData.name,
            cost: costNumber,
            unitId,
          }))
        : [
            {
              feeType: newFeeData.feeType,
              name: newFeeData.name,
              cost: costNumber,
            },
          ];
    formik.setFieldValue('fees', [...formik.values.fees, ...newLineItems]);

    // Persist the fee as a saved template when asked. Fire-and-forget: the fee
    // is already on the bill either way, and the template list refreshes when
    // the save lands. Unit scope is never part of the template.
    if (saveFeeForFuture) {
      saveFeeTemplate(structureId, {
        type: newFeeData.feeType as FeeTemplateType,
        description: newFeeData.name,
        cost: costNumber,
      })
        .then(refreshSavedFees)
        .catch((err) => {
          log.error(err);
          // The fee itself is already on the bill; only the reuse copy failed.
          toast.error('The fee was added to this bill, but could not be saved for future bills.');
        });
    }

    // 6) Clear & close
    setNewFeeErr(false);
    setNewFeeData({ feeType: 'FLAT', name: '', cost: '', unitIds: [] });
    setSaveFeeForFuture(false);
    setOpenFee(false);
  };

  const handleRemoveFee = (feeToRemove: FormValues['fees'][0]) => {
    const fees = formik.values.fees;
    const index = fees.findIndex(
      (fee) =>
        fee.feeType === feeToRemove.feeType &&
        fee.name === feeToRemove.name &&
        fee.cost === feeToRemove.cost &&
        fee.unitId === feeToRemove.unitId,
    );
    if (index === -1) return;
    formik.setFieldValue('fees', [...fees.slice(0, index), ...fees.slice(index + 1)]);
  };

  const handleFeeCostChange = (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    formik.setFieldValue(`fees[${idx}].cost`, isNaN(val) ? 0 : val);
  };

  const stepTitles = {
    ADD: ['Add Water Utility Bill'],
    EDIT: ['Edit Water Utility Bill'],
  };

  const stepSubTitles = {
    ADD: [
      'Step 1: Enter Water Bill Details', // step 1
      'Step 2: Add Fees to Tenant Invoices', // step 2
      // 'Final Review', // step 3
    ],
    EDIT: [
      'Step 1: Edit Water Bill Details', // step 1
      'Step 2: Edit Fees on Tenant Invoices', // step 2
      // 'Final Review', // step 3
    ],
  };

  // filter out non-billable units and common area units, then sort by name
  const billableUnits = units
    .filter((u) => {
      const flags: string[] = Array.isArray(u.unit_flags) ? u.unit_flags : [u.unit_flags ?? ''];
      return !flags.includes('NON_BILLABLE') && !flags.includes('COMMON_AREA');
    })
    .sort((a, b) => naturalCompare(a.name, b.name));

  return (
    <>
      <GlobalStyles
        styles={{
          // pill‐shape everywhere
          '.MuiOutlinedInput-root': {
            borderRadius: '14px !important',
            minHeight: '45px !important',
            padding: '0 !important',
          },
          '.MuiOutlinedInput-input': {
            padding: '8px 12px !important',
            lineHeight: '1.5em',
          },
          // keep your focused & hover rings
          '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 2px rgba(71,144,204,0.2) !important',
          },
          // select outlines as well
          '.MuiSelect-outlined .MuiOutlinedInput-notchedOutline': {
            borderRadius: '14px !important',
          },
        }}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth={'md'}
        fullWidth
        classes={{
          paper: classes.paper,
        }}
      >
        <Stack direction={'column'} alignItems={'center'} gap={1}>
          <DialogTitle id="form-dialog-title" fontSize={'24px'} fontWeight={700}>
            {stepTitles[type]}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          {/* BLUE PROGRESS BAR - shown for both ADD and EDIT */}
          <Box width="80%" mt={-1} mb={-1}>
            <Stack direction="row" spacing={1}>
              {[0, 1].map((_, idx) => (
                <Box
                  key={idx}
                  flex={1}
                  height={10}
                  borderRadius={3}
                  bgcolor={idx <= activeStep ? 'primary.main' : '#E0E0E0'}
                />
              ))}
            </Stack>
          </Box>
          {/* Subtitles for Page */}
          <>
            <Typography variant="subtitle1" fontWeight={500} textAlign="center" sx={{ mt: 2 }}>
              {stepSubTitles[type][activeStep] || ''}
            </Typography>
            <Typography variant="body2" textAlign="center">
              {
                {
                  0:
                    type === 'EDIT'
                      ? 'Update your water utility bill details.'
                      : 'Enter your water utility bill details to allocate usage costs based on actual consumption.',
                  1:
                    type === 'EDIT'
                      ? 'Add, edit, or remove fees on tenant invoices.'
                      : "After entering your water bill details, this step lets you add any fixed fees that should appear on every tenant's invoice.",
                }[activeStep]
              }
            </Typography>
          </>
        </Stack>
        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey', marginTop: 2 }} />
        <DialogContent>
          {activeStep === 0 ? (
            <>
              <Typography fontSize={'20px'} fontWeight={500} marginTop={2}>
                Utility Bill Information
              </Typography>
              {(formik.errors.startDate ||
                formik.errors.endDate ||
                formik.errors.volume ||
                formik.errors.cost ||
                formik.errors.effectiveRate) && (
                <Alert
                  severity="error"
                  icon={<ErrorOutlineIcon />}
                  sx={{
                    mt: 2,
                    mb: 2,
                    borderRadius: 3,
                  }}
                >
                  Please complete all required fields before continuing.
                </Alert>
              )}
              <FormInputWrapper marginTop={2}>
                <Typography fontSize="16px" fontWeight={400} marginBottom={1}>
                  Meter Read Dates
                  <Tooltip title="Your meter read dates are often the same as your billing period.">
                    <IconButton size="small" sx={{ p: 0.5 }}>
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: '17px',
                          color: '#555',
                          ml: 0.5,
                          mb: 0.25,
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <InputRowWrapper>
                  <StyledRange
                    format={'MMMM D, YYYY'}
                    // outline red when error
                    style={{
                      border:
                        formik.errors.startDate || formik.errors.endDate ? '1px solid #e53935' : '',
                    }}
                    maxDate={dayjs().tz(timeZone).startOf('day')}
                    onChange={(value, dateString) => handleDateChange(dateString)}
                    defaultValue={
                      formik.values.startDate && formik.values.endDate
                        ? [
                            dayjs.tz(formik.values.startDate, timeZone),
                            dayjs.tz(formik.values.endDate, timeZone),
                          ]
                        : undefined
                    }
                  />
                </InputRowWrapper>
                {(formik.errors.startDate || formik.errors.endDate) && (
                  <Typography
                    fontSize="16px"
                    fontWeight={400}
                    variant="body2"
                    sx={{ color: '#e53935' }}
                  >
                    This field is required
                  </Typography>
                )}
              </FormInputWrapper>
              <FormInputWrapper>
                <Typography fontSize="16px" fontWeight={400} mb={-1} mt={1}>
                  Water Usage
                  <Typography component="span" fontSize="12px" fontWeight={400} sx={{ ml: 0.5 }}>
                    (also called Consumption)*
                  </Typography>
                </Typography>
                <InputRowWrapper>
                  <Stack>
                    <TextField
                      margin="dense"
                      name="volume"
                      type="number"
                      placeholder="0"
                      value={formik.values.volume}
                      onChange={formik.handleChange}
                      variant="outlined"
                      error={!!formik.errors.volume}
                      sx={{
                        width: 300,
                        // the input itself
                        '& .MuiOutlinedInput-input': {
                          padding: '8px 12px', // vertical padding
                          height: '1.5em', // font height
                          fontSize: '1rem',
                          marginLeft: '0.5em', // left margin for the input text
                        },
                      }}
                    />
                    {formik.errors.volume && (
                      <Typography
                        fontSize="16px"
                        fontWeight={400}
                        variant="body2"
                        sx={{ color: '#e53935' }}
                      >
                        This field is required
                      </Typography>
                    )}
                  </Stack>

                  <FormControl
                    variant="outlined"
                    margin="dense"
                    sx={{
                      width: 300,
                      // the input itself
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 12px', // vertical padding
                        height: '1.5em', // font height
                        fontSize: '1rem',
                        marginLeft: '0.5em', // left margin for the input text
                      },
                    }}
                  >
                    <Select value={formik.values.unit} name="unit" onChange={formik.handleChange}>
                      {(
                        [
                          'GALLONS',
                          'GALLONS_100',
                          'GALLONS_1K',
                          'LITERS',
                          'LITERS_1K',
                          'CF',
                          'CCF',
                        ] as Array<keyof typeof UNIT_LABELS>
                      ).map((value) => (
                        <MenuItem key={value} value={value}>
                          {UNIT_LABELS[value]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </InputRowWrapper>
              </FormInputWrapper>
              <FormInputWrapper>
                <Typography fontSize="16px" fontWeight={400}>
                  Total Cost
                  <Tooltip title="Enter the amount of utility bill to split among the tenants. Typically the total cost.">
                    <IconButton size="small" sx={{ p: 0.5 }}>
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: '17px',
                          color: '#555',
                          ml: 0.5,
                          mb: 0.25,
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <InputRowWrapper>
                  <Stack>
                    <TextField
                      error={!!formik.errors.cost}
                      margin="none"
                      type="number"
                      name="cost"
                      placeholder="0.00"
                      value={formik.values.cost}
                      onChange={formik.handleChange}
                      variant="outlined"
                      sx={{
                        width: 300,
                        // the input itself
                        '& .MuiOutlinedInput-input': {
                          padding: '8px 12px', // vertical padding
                          height: '1.5em', // font height
                          fontSize: '1rem',
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            style={{ marginLeft: 20, marginRight: 0 }}
                          >
                            <span
                              style={{
                                fontWeight: 500, // bold
                                color: '#232323', // dark grey
                                fontFamily: 'inherit',
                                display: 'inline-block',
                                textAlign: 'center',
                              }}
                            >
                              $
                            </span>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {formik.errors.cost && (
                      <Typography
                        fontSize="16px"
                        fontWeight={400}
                        variant="body2"
                        sx={{ color: '#e53935' }}
                      >
                        This field is required
                      </Typography>
                    )}
                  </Stack>
                </InputRowWrapper>
              </FormInputWrapper>
              <>
                <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey', mt: 3 }} />
                <Typography fontSize={'20px'} fontWeight={500} marginTop={2}>
                  Tenant Invoice Details
                </Typography>
                <FormInputWrapper marginTop={2}>
                  <InputRowWrapper>
                    {stripeConnect && (
                      <Box sx={{ width: 300 }}>
                        <Typography fontSize="16px" fontWeight={400} mb={1}>
                          Invoice Due Date
                          <Tooltip title="The due date will be set 21 days after the bill is created.">
                            <IconButton size="small" sx={{ p: 0.5 }}>
                              <InfoOutlinedIcon
                                sx={{
                                  fontSize: '17px',
                                  color: '#555',
                                  ml: 0.5,
                                  mb: 0.25,
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                        <StyledDate
                          value={
                            formik.values.dueDate ? dayjs.tz(formik.values.dueDate, timeZone) : null
                          }
                          onChange={(_, dateString) => handleDueDateChange(dateString)}
                          disabled
                        />
                      </Box>
                    )}
                    <Box sx={{ width: 300 }}>
                      <Typography fontSize="16px" fontWeight={400} mb={1}>
                        Rate Method
                        <Tooltip title={METHOD_DESCRIPTION[formik.values.rateMethod]}>
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: '17px',
                                color: '#555',
                                ml: 0.5,
                                mb: 0.25,
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Typography>
                      <FormControl variant="outlined" sx={{ width: 300, height: 45 }}>
                        <Select
                          value={formik.values.rateMethod}
                          onChange={formik.handleChange}
                          name="rateMethod"
                          sx={{ borderRadius: 3, minHeight: 45 }}
                        >
                          <MenuItem value="SIMPLESUB">SimpleSUB Effective</MenuItem>
                          <MenuItem value="UTILITY">Utility Effective</MenuItem>
                          <MenuItem value="CUSTOM">Custom</MenuItem>
                        </Select>
                      </FormControl>
                      {formik.values.rateMethod === 'CUSTOM' && (
                        <FormInputWrapper>
                          <Typography fontSize="16px" fontWeight={400} mt={1}>
                            Bill Rate
                          </Typography>
                          <InputRowWrapper>
                            <Stack>
                              <Box sx={{ width: 300 }}>
                                <TextField
                                  fullWidth
                                  margin="dense"
                                  variant="outlined"
                                  name="effectiveRate"
                                  type="number"
                                  placeholder="0.00"
                                  value={formik.values.effectiveRate ?? ''}
                                  onBlur={formik.handleBlur}
                                  onChange={formik.handleChange}
                                  error={
                                    formik.values.rateMethod === 'CUSTOM' &&
                                    !!formik.errors.effectiveRate
                                  }
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start" sx={{ ml: 2, mr: 0 }}>
                                        <Typography
                                          component="span"
                                          sx={{
                                            fontWeight: 500, // matches your other dollar signs
                                            color: '#232323', // same dark‐grey
                                            fontFamily: 'inherit', // inherit your form font
                                          }}
                                        >
                                          $
                                        </Typography>
                                      </InputAdornment>
                                    ),
                                    endAdornment: (
                                      <InputAdornment position="end" sx={{ pr: 2, pl: 0 }}>
                                        /{' '}
                                        {
                                          UNIT_LABELS[
                                            formik.values.unit as keyof typeof UNIT_LABELS
                                          ]
                                        }
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      height: 45, // exactly the same height
                                      borderRadius: 14, // pill shape
                                    },
                                    '& .MuiOutlinedInput-input': {
                                      padding: '8px 0', // vertical padding only (adornments handle horizontal)
                                    },
                                  }}
                                />
                              </Box>

                              {formik.values.rateMethod === 'CUSTOM' &&
                                formik.errors.effectiveRate && (
                                  <Typography
                                    fontSize="16px"
                                    fontWeight={400}
                                    variant="body2"
                                    sx={{ color: '#e53935' }}
                                  >
                                    This field is required
                                  </Typography>
                                )}
                            </Stack>
                          </InputRowWrapper>
                        </FormInputWrapper>
                      )}
                    </Box>
                  </InputRowWrapper>
                </FormInputWrapper>
              </>
            </>
          ) : activeStep === 1 ? (
            // Step 2: Add Fees
            <>
              {savedFees.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Saved fees:
                  </Typography>
                  {savedFees.map((template) => (
                    <Tooltip key={template.id} title={FEE_TYPE_LABELS[template.type]} arrow>
                      <Chip
                        icon={<AddIcon />}
                        label={`${template.description} · $${Number(template.cost).toFixed(2)}`}
                        variant="outlined"
                        color="primary"
                        onClick={() => handleAddSavedFee(template)}
                      />
                    </Tooltip>
                  ))}
                  <Button size="small" onClick={() => setOpenManageFees(true)}>
                    Manage
                  </Button>
                </Box>
              )}
              <StyledFeesTable component={Paper}>
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '30%' }} align="center">
                        Description
                      </TableCell>
                      <TableCell sx={{ width: '20%' }} align="center">
                        Type
                      </TableCell>
                      <TableCell sx={{ width: '20%' }} align="center">
                        Applied to
                      </TableCell>
                      <TableCell sx={{ width: '20%' }} align="center">
                        Cost
                      </TableCell>
                      <TableCell sx={{ width: 'auto' }} /> {/* blank header for trash icon */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formik.values.fees.map((fee, idx) => (
                      <TableRow key={idx}>
                        {/* Every column edits in place: a wrong description or fee
                          type is corrected on the row instead of delete-and-re-add */}
                        <TableCell align="center">
                          <TextField
                            variant="standard"
                            size="small"
                            value={fee.name}
                            error={fee.name.trim() === ''}
                            helperText={
                              showFeeErrors && fee.name.trim() === '' ? 'Description required' : ''
                            }
                            inputProps={{ 'aria-label': `Description for fee ${idx + 1}` }}
                            onChange={(e) =>
                              formik.setFieldValue(`fees[${idx}].name`, e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Select
                            variant="standard"
                            size="small"
                            value={fee.feeType}
                            inputProps={{ 'aria-label': `Type for fee ${idx + 1}` }}
                            onChange={(e) => {
                              const newType = e.target.value as string;
                              formik.setFieldValue(`fees[${idx}].feeType`, newType);
                              // Unit scope only applies to unit-flat fees; switching
                              // away clears it, switching to it prompts in this row
                              if (newType !== 'UNIT_FLAT') {
                                formik.setFieldValue(`fees[${idx}].unitId`, undefined);
                              }
                            }}
                          >
                            <MenuItem value="FLAT">Flat Fee</MenuItem>
                            <MenuItem value="SPLIT">Split Fee</MenuItem>
                            <MenuItem value="UNIT_FLAT">Unit-Flat Fee</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell align="center">
                          {fee.feeType === 'UNIT_FLAT' ? (
                            <Select
                              variant="standard"
                              size="small"
                              displayEmpty
                              value={fee.unitId ?? ''}
                              error={!fee.unitId}
                              inputProps={{ 'aria-label': `Unit for fee ${idx + 1}` }}
                              onChange={(e) =>
                                formik.setFieldValue(`fees[${idx}].unitId`, e.target.value)
                              }
                            >
                              <MenuItem value="" disabled>
                                Select unit
                              </MenuItem>
                              {billableUnits.map((u) => (
                                <MenuItem key={u.id} value={u.id}>
                                  {u.name}
                                </MenuItem>
                              ))}
                            </Select>
                          ) : (
                            'All units'
                          )}
                          {fee.feeType === 'UNIT_FLAT' && showFeeErrors && !fee.unitId && (
                            <FormHelperText error sx={{ textAlign: 'center' }}>
                              Choose a unit
                            </FormHelperText>
                          )}
                        </TableCell>

                        {/* ← Inline editable cost cell */}
                        <TableCell align="center" sx={{ pr: 1 }}>
                          <TextField
                            type="number"
                            size="small"
                            value={fee.cost}
                            onChange={handleFeeCostChange(idx)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start" sx={{ pl: 1.5, pr: 0 }}>
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontWeight: 500, // bold
                                      color: '#232323', // dark grey
                                      fontFamily: 'inherit',
                                    }}
                                  >
                                    $
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              width: 100,
                              // match your “small” TextField shape if needed:
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 8,
                                height: 32,
                                width: 120,
                              },
                              '& .MuiOutlinedInput-input': {
                                padding: '4px 8px',
                              },
                            }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleRemoveFee(fee)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}

                    {formik.values.fees.length === 0 && (
                      <TableRow
                        sx={{
                          height: '48px',
                        }}
                      >
                        <TableCell colSpan={5} align="center">
                          <NoRows title="Start adding fees" />
                        </TableCell>
                      </TableRow>
                    )}

                    <TableRow>
                      <TableCell colSpan={5} sx={{ borderBottom: 'none', py: 1 }}>
                        <Box
                          onClick={handleClickMenu}
                          sx={{
                            color: 'primary.main',
                            fontWeight: 500,
                            cursor: 'pointer',
                          }}
                        >
                          + Add Fee
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </StyledFeesTable>
              {showFeeErrors &&
                formik.values.fees.some(
                  (fee) => fee.name.trim() === '' || (fee.feeType === 'UNIT_FLAT' && !fee.unitId),
                ) && (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    Finish the highlighted fees before creating the bill: every fee needs a
                    description, and unit-flat fees need a unit selected.
                  </Alert>
                )}
            </>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <caption>{noDevices && <NoRows title="All devices OK" />}</caption>
                <TableHead>
                  <TableRow>
                    <TableCell>Unit Name</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Last Meter Read</TableCell>
                    <TableCell>Estimated Usage</TableCell>
                    <TableCell>Volume Unit</TableCell>
                  </TableRow>
                </TableHead>
              </Table>
            </TableContainer>
          )}
          <Box
            sx={{
              mt: 2,
              px: 1,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#E6F7FF', // blue background
              color: '#096DD9', // dark-blue text
              borderRadius: 3,
            }}
          >
            <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.7 }} />
            <Typography component="span">
              {activeStep === 0
                ? type === 'EDIT'
                  ? 'After updating the bill details, click "Next" to edit fees.'
                  : 'After entering the bill, click "Next" to add any additional fees (e.g., service fees, flat charges) before finalizing the bill.'
                : activeStep === 1
                ? type === 'EDIT'
                  ? 'Once you\'ve updated the fees, click "Save" to apply your changes.'
                  : 'Once you\'ve added the appropriate charges, click "Create Bill" to finalize and review tenant usage.'
                : ''}
            </Typography>
          </Box>
        </DialogContent>
        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey' }} />
        <DialogActions>
          <SWLoadingButtonOutlined
            sx={{
              minWidth: 120,
            }}
            onClick={() => {
              if (activeStep === 0) {
                handleClose();
                return;
              }
              setActiveStep(activeStep - 1);
            }}
          >
            {activeStep === 0 ? 'Close' : 'Back'}
          </SWLoadingButtonOutlined>
          <SWLoadingButton
            sx={{
              minWidth: 120,
            }}
            onClick={handleNext}
            loading={loading}
          >
            {
              activeStep !== steps.length - 1
                ? 'Next' // advance to next step
                : type === 'EDIT'
                ? 'Save' // edit mode final step
                : 'Create Bill' // add mode final step
            }
          </SWLoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openFee}
        onClose={handleFeeClose}
        aria-labelledby="form-dialog-title"
        maxWidth={'sm'}
        fullWidth
        // add rounded corners to the form
        classes={{
          paper: classes.paper,
        }}
      >
        <Stack direction={'column'} alignItems={'center'} gap={1}>
          <DialogTitle id="form-dialog-title" fontSize={'24px'} fontWeight={700}>
            {`Add ${FEE_TYPE_LABELS[newFeeData.feeType] || 'Fee'}`}
            <IconButton
              aria-label="close"
              onClick={handleFeeClose}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider
            variant="fullWidth"
            sx={{ borderWidth: 1, borderColor: 'grey', width: '100%' }}
          />
        </Stack>
        <DialogContent>
          <Stack sx={{ px: 3 }}>
            {/* 1) Fee Type dropdown (always visible) */}
            <Typography variant="subtitle2" gutterBottom>
              Type
            </Typography>
            <FormControl fullWidth>
              <TextField
                select
                fullWidth
                value={newFeeData.feeType}
                sx={{ mb: 1 }}
                onChange={(e) =>
                  setNewFeeData({ ...newFeeData, feeType: e.target.value as string })
                }
                variant="outlined"
              >
                <MenuItem value="FLAT">Flat</MenuItem>
                <MenuItem value="SPLIT">Split</MenuItem>
                <MenuItem value="UNIT_FLAT">Unit Flat</MenuItem>
              </TextField>
            </FormControl>
            {/* Fee Type description */}
            <DialogContentText variant="body2" color="textSecondary" sx={{ mb: 3, px: 3 }}>
              {FEE_TYPE_DESC[newFeeData.feeType] || 'Add a new fee to the bill.'}
            </DialogContentText>
            <Divider
              variant="fullWidth"
              sx={{ borderWidth: 1, borderColor: 'grey', width: '100%' }}
            />

            {/* 2) Fee Description */}
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Description
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter description"
              value={newFeeData.name}
              onChange={(e) => setNewFeeData({ ...newFeeData, name: e.target.value })}
              error={newFeeErr && !newFeeData.name}
              helperText={newFeeErr && !newFeeData.name ? 'Required' : ''}
              variant="outlined"
            />

            {/* 3) Apply To, only for UNIT_FLAT. Multi-select so a single
                Unit-Flat fee can be applied to many units at once (e.g. "$6
                across all units except the one with a different rate"). Each
                selected unit becomes its own UNIT_FLAT line item on submit. */}
            {newFeeData.feeType === 'UNIT_FLAT' && (
              <>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                  sx={{ mt: 2 }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Apply To
                  </Typography>
                  <Typography
                    variant="caption"
                    onClick={() => {
                      const allSelected =
                        newFeeData.unitIds.length === billableUnits.length &&
                        billableUnits.length > 0;
                      setNewFeeData({
                        ...newFeeData,
                        unitIds: allSelected ? [] : billableUnits.map((u) => u.id),
                      });
                    }}
                    sx={{
                      cursor: 'pointer',
                      color: 'primary.main',
                      userSelect: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {newFeeData.unitIds.length === billableUnits.length && billableUnits.length > 0
                      ? 'Clear all'
                      : 'Select all'}
                  </Typography>
                </Stack>
                <FormControl fullWidth error={newFeeErr && newFeeData.unitIds.length === 0}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={billableUnits}
                    getOptionLabel={(u) => u.name}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    value={billableUnits.filter((u) => newFeeData.unitIds.includes(u.id))}
                    onChange={(_, selection) =>
                      setNewFeeData({
                        ...newFeeData,
                        unitIds: selection.map((u) => u.id),
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          newFeeData.unitIds.length === 0 ? 'Select one or more units' : ''
                        }
                        variant="outlined"
                        error={newFeeErr && newFeeData.unitIds.length === 0}
                        helperText={
                          newFeeErr && newFeeData.unitIds.length === 0
                            ? 'Select at least one unit'
                            : ''
                        }
                      />
                    )}
                  />
                </FormControl>
              </>
            )}

            {/* 4) Cost */}
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Cost
            </Typography>
            <TextField
              fullWidth
              type="number"
              inputProps={{
                step: '0.00', // allow decimals
                inputMode: 'decimal', // mobile friendly
                pattern: '[0-9]+([.,][0-9]+)?',
              }}
              placeholder="0.00"
              value={newFeeData.cost}
              onChange={(e) => setNewFeeData({ ...newFeeData, cost: e.target.value })}
              error={newFeeErr && !newFeeData.cost}
              helperText={newFeeErr && !newFeeData.cost ? 'Required' : ''}
              variant="outlined"
              sx={{
                // the input itself
                '& .MuiOutlinedInput-input': {
                  marginLeft: '0.5em', // left margin for the input text
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" style={{ marginLeft: 20, marginRight: 0 }}>
                    <span
                      style={{
                        fontWeight: 500, // bold
                        color: '#232323', // dark grey
                        fontFamily: 'inherit',
                        display: 'inline-block',
                        textAlign: 'center',
                      }}
                    >
                      $
                    </span>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={saveFeeForFuture}
                  onChange={(e) => setSaveFeeForFuture(e.target.checked)}
                />
              }
              label="Save this fee for future bills on this property"
            />
          </Stack>
        </DialogContent>
        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey', width: '100%' }} />
        <DialogActions>
          <SWLoadingButtonOutlined
            sx={{
              minWidth: 120,
            }}
            onClick={handleFeeClose}
          >
            Cancel
          </SWLoadingButtonOutlined>
          <SWLoadingButton
            sx={{
              minWidth: 120,
            }}
            onClick={handleAddFee}
          >
            Add Fee
          </SWLoadingButton>
        </DialogActions>
      </Dialog>

      {/* Manage Saved Fees: deletion lives here, in normal focus order, rather
          than nested inside the Add Fee menu's items */}
      <Dialog
        open={openManageFees}
        onClose={handleManageFeesClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <Stack direction={'column'} alignItems={'center'} gap={1}>
          <DialogTitle fontSize={'24px'} fontWeight={700}>
            Saved Fees
            <IconButton
              aria-label="close"
              onClick={handleManageFeesClose}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider
            variant="fullWidth"
            sx={{ borderWidth: 1, borderColor: 'grey', width: '100%' }}
          />
        </Stack>
        <DialogContent>
          {savedFees.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No saved fees yet. Check "Save this fee for future bills" when adding a fee to keep it
              here.
            </Typography>
          ) : (
            <List disablePadding>
              {savedFees.map((template, idx) => (
                <ListItem
                  key={template.id}
                  divider={idx < savedFees.length - 1}
                  secondaryAction={
                    deleteArmedId === template.id ? (
                      <Button
                        size="small"
                        color="error"
                        // The arming IconButton unmounts when this replaces it;
                        // take its focus so keyboard users land on Confirm
                        autoFocus
                        onClick={() => handleDeleteSavedFee(template)}
                      >
                        Confirm Remove
                      </Button>
                    ) : (
                      <IconButton
                        edge="end"
                        aria-label={`Remove ${template.description} from saved fees`}
                        onClick={() => setDeleteArmedId(template.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  <ListItemText
                    primary={template.description}
                    secondary={`${FEE_TYPE_LABELS[template.type]} · $${Number(
                      template.cost,
                    ).toFixed(2)}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey', width: '100%' }} />
        <DialogActions>
          <SWLoadingButtonOutlined sx={{ minWidth: 120 }} onClick={handleManageFeesClose}>
            Close
          </SWLoadingButtonOutlined>
        </DialogActions>
      </Dialog>
    </>
  );
};
